import { useRef, useState, useEffect } from 'react';
import { useOnScreen } from '../../hooks/useOnScreen';

interface ScoreGaugeProps {
  score: number;
  label: string;
  color: string;
  glowColor: string;
  animated?: boolean;
}

export function ScoreGauge({ score, label, color, glowColor, animated = false }: ScoreGaugeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useOnScreen(ref);
  const [displayScore, setDisplayScore] = useState(animated ? 0 : score);
  const started = useRef(false);

  useEffect(() => {
    if (!animated) {
      setDisplayScore(score);
      return;
    }
    if (!visible || started.current) return;
    started.current = true;
    const startTime = performance.now();
    const duration = 2200;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.floor(score * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else setDisplayScore(score);
    };
    requestAnimationFrame(animate);
  }, [visible, score, animated]);

  const radius = 70;
  const circumference = Math.PI * radius;
  const maxScore = 850;
  const minScore = 300;
  const arcScore = animated ? Math.min(maxScore, Math.max(minScore, displayScore)) : score;
  const percentage = (arcScore - minScore) / (maxScore - minScore);
  const dashOffset = circumference - circumference * percentage;

  return (
    <div ref={ref} className="flex flex-col items-center">
      <div className="relative" style={{ width: 160, height: 100 }}>
        <svg width="160" height="100" viewBox="0 0 160 100" className="overflow-visible">
          <path
            d="M 10 90 A 70 70 0 0 1 150 90"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M 10 90 A 70 70 0 0 1 150 90"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${circumference}`}
            strokeDashoffset={visible ? dashOffset : circumference}
            style={{
              transition: animated ? 'stroke-dashoffset 0.12s linear' : 'stroke-dashoffset 2s ease-out',
              filter: `drop-shadow(0 0 8px ${glowColor})`,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
          <span
            className="font-bold"
            style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: 40,
              fontWeight: 800,
              color: color,
              lineHeight: 1,
              filter: `drop-shadow(0 0 12px ${glowColor})`,
            }}
          >
            {animated ? displayScore : score}
          </span>
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              fontWeight: 600,
              color: color,
              letterSpacing: 3,
              textTransform: 'uppercase' as const,
              marginTop: 2,
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}
